// Utility functions to compute inspection tree from WorkPlanTemplate objects

import { getTestStatusDetails } from "../../PiScheduler/getTestStatusUtils";

const INSPECTION_STATUS_PRIORITY = ["overdue", "expiring", "due", "upcoming"];

const normalizeStatus = (value) => {
  if (!value && value !== 0) {
    return "upcoming";
  }
  const lower = String(value).toLowerCase();
  if (lower.includes("overdue")) return "overdue";
  if (lower.includes("expiring")) return "expiring";
  if (lower.includes("due")) return "due";
  if (lower.includes("upcoming")) return "upcoming";
  return "upcoming";
};

const parseDate = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTestStatus = (test) => {
  if (!test) return "upcoming";

  if (test.status) {
    return normalizeStatus(test.status);
  }

  if (test.completed === true || test.completed === "true") {
    // if completed then not overdue
    return "upcoming";
  }

  if (test.overdue || test.overdueDate) {
    const overdueDate = parseDate(test.overdueDate || test.overdue);
    if (overdueDate) {
      if (overdueDate <= new Date()) return "overdue";
    }
  }

  const expiryDate = parseDate(test.nextExpiryDate || test.expiryDate || test.overdue);
  if (expiryDate) {
    const now = new Date();
    if (expiryDate < now) {
      return "due";
    }
    const diffMs = expiryDate - now;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays <= 7) {
      return "expiring";
    }
    return "upcoming";
  }

  const dueDate = parseDate(test.nextDueDate || test.dueDate);
  if (dueDate) {
    const now = new Date();
    if (dueDate < now) {
      return "due";
    }
    return "upcoming";
  }

  return "upcoming";
};

const pickWorstStatus = (statuses) => {
  if (!Array.isArray(statuses) || statuses.length === 0) return "upcoming";
  return statuses.reduce((worst, status) => {
    const currentIdx = INSPECTION_STATUS_PRIORITY.indexOf(normalizeStatus(status));
    const bestIdx = INSPECTION_STATUS_PRIORITY.indexOf(normalizeStatus(worst));
    if (currentIdx === -1) return worst;
    if (bestIdx === -1) return status;
    return currentIdx < bestIdx ? status : worst;
  }, "upcoming");
};

const evaluateInspectionStatus = (assets) => {
  if (!Array.isArray(assets) || assets.length === 0) return "upcoming";
  const statuses = assets.map((asset) => normalizeStatus(asset.status));
  return pickWorstStatus(statuses);
};

const isConfigurationAsset = (assetType) => {
  if (!assetType || typeof assetType !== "string") return false;
  const configurationTypes = ["configuration", "config", "shelf", "nested"];
  return configurationTypes.some((t) => assetType.toLowerCase().includes(t));
};

const isInspectableAsset = (unit) => {
  if (!unit) return false;
  if (unit.testForm && Array.isArray(unit.testForm) && unit.testForm.length > 0) {
    return true;
  }
  if (unit.assetType && typeof unit.assetType === "string") {
    return !isConfigurationAsset(unit.assetType);
  }
  return !!unit.unitId;
};

const createAssetNode = (unit, groupedData, ungroupedData, status) => {
  if (!unit || !unit.id || !unit.unitId) return null;

  return {
    id: unit.id,
    title: unit.unitId,
    assetType: unit.assetType || "",
    status,
    unGroupedChildren: ungroupedData,
    groupedChildren: groupedData,
    raw: unit,
  };
};

const getInspectionTests = (unit) => {
  const testGroupMap = {};
  const grouped = [];
  const ungroupedTests = [];

  if (!unit || !unit.testForm || !Array.isArray(unit.testForm)) {
    return { testGroups: [], groupedAssetTests: [], ungroupedAssetTests: [] };
  }

  unit.testForm.forEach((rawTest) => {
    if (!rawTest) return;

    const { status, statusLine1, statusLine2 } = getTestStatusDetails(rawTest);

    const test = {
      id: rawTest.testID || rawTest.testId || rawTest.id || "",
      title: rawTest.title || rawTest.name || "",
      status: status || getTestStatus(rawTest),
      statusLine1: statusLine1 || "",
      statusLine2: statusLine2 || "",
      formCode: rawTest.testCode || rawTest.code || "",
      raw: rawTest,
    };

    const group = rawTest.testGroup || rawTest.group || null;
    if (group && group.code) {
      if (!testGroupMap[group.code]) {
        testGroupMap[group.code] = {
          code: group.code,
          description: group.description || group.name || group.code,
          tests: [],
          status: "upcoming",
        };
      }
      testGroupMap[group.code].tests.push(test);
      testGroupMap[group.code].status = pickWorstStatus([testGroupMap[group.code].status, test.status]);
    } else {
      ungroupedTests.push(test);
    }
  });

  const groupedAssetTests = Object.values(testGroupMap);

  return {
    testGroups: groupedAssetTests.map((tg) => ({ code: tg.code, description: tg.description })),
    groupedAssetTests,
    ungroupedAssetTests: ungroupedTests,
  };
};

const getInspectionTree = (plans = [], assetTypesToDisplay) => {
  if (!Array.isArray(plans)) return [];

  return plans
    .map((plan) => {
      const planId = plan.id || plan._id || plan.workplanTemplateId || "";
      const title = plan.title || plan.name || "";
      const firstTask = Array.isArray(plan.tasks) && plan.tasks.length > 0 ? plan.tasks[0] : null;
      let units = firstTask && Array.isArray(firstTask.units) ? firstTask.units : [];

      if (!planId || !title || units.length === 0) return null;

      let parentAsset = null;
      const assetTypesSet = new Set();
      const testGroupsSet = new Set();
      units = units.filter((u) => assetTypesToDisplay.includes(u.assetType));
      let planUnits = units
        .map((unit) => {
          if (!isInspectableAsset(unit) && !isConfigurationAsset(unit.assetType)) {
            parentAsset = unit;
          }

          if (unit && unit.assetType) {
            assetTypesSet.add(unit.assetType);
          }

          const { testGroups, groupedAssetTests, ungroupedAssetTests } = getInspectionTests(unit);
          testGroups.forEach((tg) => testGroupsSet.add(JSON.stringify(tg)));

          const unitStatus =
            groupedAssetTests.length > 0
              ? pickWorstStatus(groupedAssetTests.map((g) => g.status))
              : pickWorstStatus(ungroupedAssetTests.map((t) => t.status));

          return createAssetNode(unit, groupedAssetTests, ungroupedAssetTests, unitStatus);
        })
        .filter((asset) => asset !== null);

      planUnits = planUnits.sort((a, b) => {
        const statusA = INSPECTION_STATUS_PRIORITY.indexOf(normalizeStatus(a.status));
        const statusB = INSPECTION_STATUS_PRIORITY.indexOf(normalizeStatus(b.status));
        if (statusA !== statusB) return statusA - statusB;

        const expiryA = parseDate(
          a && Array.isArray(a.unGroupedChildren) && a.unGroupedChildren.length > 0 && a.unGroupedChildren[0]
            ? a.unGroupedChildren[0].expiryDate || ""
            : "",
        );
        const expiryB = parseDate(
          b && Array.isArray(b.unGroupedChildren) && b.unGroupedChildren.length > 0 && b.unGroupedChildren[0]
            ? b.unGroupedChildren[0].expiryDate || ""
            : "",
        );
        if (expiryA && expiryB) return expiryA - expiryB;
        if (expiryA) return -1;
        if (expiryB) return 1;
        return 0;
      });

      if (!parentAsset) {
        parentAsset = units.find((unit) => unit && unit.id && unit.unitId && !unit.testForm);
      }

      if (!parentAsset) {
        // allow no parent asset for safety
        parentAsset = { id: planId, unitId: title };
      }

      const inspectionStatus = evaluateInspectionStatus(planUnits);

      const testGroups = Array.from(testGroupsSet).map((s) => JSON.parse(s)) || [];
      const assetTypes = Array.from(assetTypesSet);

      return {
        id: planId,
        title,
        status: inspectionStatus,
        locationId: parentAsset.id || "",
        locationName: parentAsset.unitId || parentAsset.title || "",
        inspectionState: "fresh",
        children: planUnits,
        userRole: plan.userRole || null,
        testGroups,
        assetTypes,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const statusA = INSPECTION_STATUS_PRIORITY.indexOf(normalizeStatus(a.status));
      const statusB = INSPECTION_STATUS_PRIORITY.indexOf(normalizeStatus(b.status));
      if (statusA !== statusB) return statusA - statusB;

      return 0;
    });
};

const getInspectionSummary = (inspectionTree = []) => {
  const totalPlans = inspectionTree.length;
  const statusCounts = inspectionTree.reduce(
    (counts, plan) => {
      const status = normalizeStatus(plan.status);
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    },
    { overdue: 0, expiring: 0, due: 0, upcoming: 0 },
  );

  return {
    totalPlans,
    ...statusCounts,
  };
};

export { getInspectionTree, getInspectionSummary, getTestStatus, evaluateInspectionStatus };



// WEBPACK FOOTER //
// ./src/components/InspectionSchedule/utils/getInspectionTree.js